"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DialogContent } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useCreateCustomOrder } from "@/lib/api/create-custom-order/use-mutation"
import { StorefrontRoutesMap } from "@repo/api-shared"
import { useSendCallRequest } from "@/lib/api/send-call-request/use-mutation"

function BaseDialog({
    open,
    onOpenChange,
    dialogTitle,
    formTitle,
    buttonText,
    onSubmit,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    dialogTitle: string
    formTitle: React.ReactNode
    buttonText: string
    onSubmit: (data: {
        customer_name: string
        customer_phone: string
    }) => Promise<void>
}) {
    const router = useRouter()
    const [name, setName] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("customerName") || ""
        }
        return ""
    })
    const [phone, setPhone] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("customerPhone") || ""
        }
        return ""
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setName(value)
        localStorage.setItem("customerName", value)
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setPhone(value)
        localStorage.setItem("customerPhone", value)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setError(null)

        try {
            await onSubmit({
                customer_name: name,
                customer_phone: phone,
            })

            onOpenChange(false)
            router.push("/thanks")
        } catch (err) {
            console.error(err)
            setError(
                "Произошла ошибка при отправке заказа. Пожалуйста, попробуйте еще раз.",
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="border-2 border-primary sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="border-b-2 border-primary pb-2 text-xl">
                        {dialogTitle}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        {formTitle && <p className="mb-4">{formTitle}</p>}

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Ваше имя</Label>
                                <Input
                                    id="name"
                                    value={name}
                                    onChange={handleNameChange}
                                    required
                                    className="border-primary"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Номер телефона</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    required
                                    placeholder="+7 (___) ___-__-__"
                                    className="border-primary"
                                />
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600">{error}</div>
                    )}

                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary hover:bg-primary/80"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                <span>Отправка...</span>
                            </div>
                        ) : (
                            buttonText
                        )}
                    </Button>

                    <div className="text-xs text-gray-600">
                        Нажимая кнопку "{buttonText}" вы соглашаетесь с{" "}
                        <a
                            href={StorefrontRoutesMap.PRIVACY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            политикой обработки персональных данных
                        </a>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export function OrderDialog({
    open,
    onOpenChange,
    title,
    variant_id,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    variant_id: string | undefined
}) {
    const { mutateAsync: createCustomOrder } = useCreateCustomOrder()

    const handleSubmit = async (data: {
        customer_name: string
        customer_phone: string
    }) => {
        if (!variant_id) {
            throw new Error("Не удалось получить ID варианта товара")
        }

        await createCustomOrder({
            product_variant_ids: [variant_id],
            ...data,
        })
    }

    return (
        <BaseDialog
            open={open}
            onOpenChange={onOpenChange}
            dialogTitle="Оформление заказа"
            formTitle={
                <>
                    Товар: <strong>{title}</strong>
                </>
            }
            buttonText="Оформить заказ"
            onSubmit={handleSubmit}
        />
    )
}

export function CallRequestDialog({
    open,
    onOpenChange,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { mutateAsync: sendCallRequest } = useSendCallRequest()

    const handleSubmit = async (data: {
        customer_name: string
        customer_phone: string
    }) => {
        await sendCallRequest(data)
    }

    return (
        <BaseDialog
            open={open}
            onOpenChange={onOpenChange}
            dialogTitle="Заказать обратный звонок"
            formTitle={null}
            buttonText="Заказать звонок"
            onSubmit={handleSubmit}
        />
    )
}
